import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Download, X } from 'lucide-react';
import T from 'Components/Helpers/translate';
import { downloadFile } from './downloadFile';

type Props = {
	/** Download URL of the PDF (the chip's url). `?inline=true` is appended for the iframe. */
	url: string;
	/** Filename, shown in the header and used for the download. */
	name: string;
	onClose: () => void;
};

// The download endpoints serve the file inline (browser-rendered) when asked, and
// as an attachment otherwise. Append the flag for the iframe preview; keep the bare
// URL for the explicit Download action.
const withInline = (url: string): string => url + (url.includes('?') ? '&' : '?') + 'inline=true';

// Everything is styled inline and portaled to document.body deliberately:
//  - Portaling to <body> escapes the workspace's transformed ancestors (which trap
//    position:fixed and otherwise let the top nav clip the modal header) so this is
//    a true full-viewport overlay above the nav.
//  - But <body> is OUTSIDE the shadcn Shadow DOM where Tailwind lives, and that
//    Tailwind is a purged build anyway — so class-based styling wouldn't apply.
//    Hence plain inline styles, which are independent of both.
const OVERLAY: React.CSSProperties = {
	position: 'fixed',
	top: 0,
	right: 0,
	bottom: 0,
	left: 0,
	zIndex: 100020,
	background: 'rgba(2, 6, 23, 0.6)',
	cursor: 'pointer'
};

const PANEL: React.CSSProperties = {
	position: 'absolute',
	top: 24,
	right: 24,
	bottom: 24,
	left: 24,
	display: 'flex',
	flexDirection: 'column',
	background: '#ffffff',
	borderRadius: 8,
	overflow: 'hidden',
	boxShadow: '0 10px 40px rgba(0, 0, 0, 0.35)',
	cursor: 'default'
};

const HEADER: React.CSSProperties = {
	flex: '0 0 auto',
	display: 'flex',
	alignItems: 'center',
	gap: 8,
	padding: '8px 16px',
	borderBottom: '1px solid #e5e7eb',
	color: '#1a1a1a'
};

const TITLE: React.CSSProperties = {
	flex: 1,
	minWidth: 0,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	fontSize: 14,
	fontWeight: 600
};

const ICON_BTN: React.CSSProperties = {
	height: 32,
	width: 32,
	borderRadius: 9999,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	cursor: 'pointer',
	border: 0,
	background: 'transparent',
	color: '#555'
};

const PdfPreviewModal = ({ url, name, onClose }: Props) => {
	// Keep the latest onClose in a ref so the keydown listener binds once, rather
	// than re-binding on every parent re-render (the chat re-renders per token).
	const onCloseRef = useRef(onClose);
	onCloseRef.current = onClose;

	// Esc closes it — a safety net independent of the close button.
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onCloseRef.current();
		};
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, []);

	return createPortal(
		<div style={OVERLAY} onClick={onClose}>
			<div style={PANEL} onClick={e => e.stopPropagation()}>
				<div style={HEADER}>
					<span style={TITLE} title={name}>
						{name}
					</span>
					<button
						onClick={() => downloadFile(url, name || 'document.pdf')}
						type="button"
						aria-label={T('default.download')}
						title={T('default.download')}
						style={ICON_BTN}
					>
						<Download size={16} />
					</button>
					<button
						onClick={onClose}
						type="button"
						aria-label={T('assistant.close')}
						title={T('assistant.close')}
						style={ICON_BTN}
					>
						<X size={16} />
					</button>
				</div>
				{/* No `sandbox` attribute: Chrome's PDF viewer is a script-driven extension,
				    so ANY sandbox token set (incl. allow-downloads, and even
				    allow-scripts+allow-same-origin) makes the frame render the
				    broken-document icon instead of the PDF — verified in-browser. The only
				    sandbox that could render also grants scripts+same-origin (no real
				    isolation), so there's nothing to gain. The src is already a same-origin,
				    server-built PDF URL whose response headers force PDF/attachment
				    disposition, so the residual risk is low. */}
				<iframe
					src={withInline(url)}
					title={name}
					style={{ flex: 1, width: '100%', border: 0, background: '#fff' }}
				/>
			</div>
		</div>,
		document.body
	);
};

export default PdfPreviewModal;
